import React, { useState } from 'react';
import { OutlookWidgetConfig } from '../../../../../types';

interface OutlookWidgetBuilderProps {
  config: Partial<OutlookWidgetConfig>;
  onChange: (config: OutlookWidgetConfig) => void;
}

const OutlookWidgetBuilder: React.FC<OutlookWidgetBuilderProps> = ({
  config,
  onChange,
}) => {
  const [localConfig, setLocalConfig] = useState<OutlookWidgetConfig>({
    defaultTo: config.defaultTo || [],
    defaultSubject: config.defaultSubject || '',
    defaultBody: config.defaultBody || '',
    bodyType: config.bodyType || 'HTML',
    allowEditRecipients: config.allowEditRecipients !== false,
    allowEditSubject: config.allowEditSubject !== false,
    allowEditBody: config.allowEditBody !== false,
    sendMode: config.sendMode || 'compose',
    showComposeButton: config.showComposeButton !== false,
  });

  const [recipientInput, setRecipientInput] = useState('');

  const updateConfig = (updates: Partial<OutlookWidgetConfig>) => {
    const newConfig = { ...localConfig, ...updates };
    setLocalConfig(newConfig);
    onChange(newConfig);
  };

  const addRecipient = () => {
    const email = recipientInput.trim();
    if (email && !localConfig.defaultTo?.includes(email)) {
      updateConfig({ defaultTo: [...(localConfig.defaultTo || []), email] });
      setRecipientInput('');
    }
  };

  const removeRecipient = (email: string) => {
    updateConfig({
      defaultTo: localConfig.defaultTo?.filter((e) => e !== email),
    });
  };

  return (
    <div className="space-y-4">
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 text-sm text-primary-800">
        <strong>Outlook Widget</strong> - Send emails or open email composer with pre-filled content.
        Works with Microsoft account (SSO) or as simple email links.
      </div>

      {/* Send Mode */}
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          Email Mode
        </label>
        <select
          value={localConfig.sendMode}
          onChange={(e) => updateConfig({ sendMode: e.target.value as OutlookWidgetConfig['sendMode'] })}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none"
        >
          <option value="compose">Open in Outlook (Compose)</option>
          <option value="draft">Save as Draft (requires SSO)</option>
          <option value="send">Send Immediately (requires SSO)</option>
        </select>
        <p className="text-xs text-neutral-500 mt-1">
          {localConfig.sendMode === 'compose' && 'Opens Outlook with pre-filled email. No Microsoft login required.'}
          {localConfig.sendMode === 'draft' && 'Creates a draft in user\'s Outlook. Requires Microsoft account connection.'}
          {localConfig.sendMode === 'send' && 'Sends email directly. Requires Microsoft account connection.'}
        </p>
      </div>

      {/* Default Recipients */}
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          Default Recipients
        </label>
        <div className="flex gap-2 mb-2">
          <input
            type="email"
            value={recipientInput}
            onChange={(e) => setRecipientInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addRecipient()}
            placeholder="email@example.com"
            className="flex-1 px-3 py-2 border rounded-lg focus:outline-none"
          />
          <button
            onClick={addRecipient}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Add
          </button>
        </div>
        {localConfig.defaultTo && localConfig.defaultTo.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {localConfig.defaultTo.map((email) => (
              <span
                key={email}
                className="inline-flex items-center gap-1 px-2 py-1 bg-neutral-100 rounded text-sm"
              >
                {email}
                <button
                  onClick={() => removeRecipient(email)}
                  className="text-neutral-400 hover:text-danger-500"
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p className="text-xs text-neutral-500">No default recipients</p>
        )}
      </div>

      {/* Default Subject */}
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          Default Subject
        </label>
        <input
          type="text"
          value={localConfig.defaultSubject || ''}
          onChange={(e) => updateConfig({ defaultSubject: e.target.value })}
          placeholder="e.g., Q4 2026 Earnings Report Review"
          className="w-full px-3 py-2 border rounded-lg focus:outline-none"
        />
      </div>

      {/* Default Body */}
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          Default Body
        </label>
        <textarea
          value={localConfig.defaultBody || ''}
          onChange={(e) => updateConfig({ defaultBody: e.target.value })}
          placeholder="Enter the default email body content..."
          rows={4}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none resize-none"
        />
        <div className="flex items-center gap-4 mt-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="bodyType"
              value="HTML"
              checked={localConfig.bodyType === 'HTML'}
              onChange={() => updateConfig({ bodyType: 'HTML' })}
              className="w-4 h-4 text-primary-600"
            />
            <span className="text-sm text-neutral-700">HTML</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="bodyType"
              value="Text"
              checked={localConfig.bodyType === 'Text'}
              onChange={() => updateConfig({ bodyType: 'Text' })}
              className="w-4 h-4 text-primary-600"
            />
            <span className="text-sm text-neutral-700">Plain Text</span>
          </label>
        </div>
      </div>

      {/* Edit Permissions */}
      <div className="border rounded-lg p-3 bg-neutral-50">
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          Allow Workers to Edit
        </label>
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={localConfig.allowEditRecipients !== false}
              onChange={(e) => updateConfig({ allowEditRecipients: e.target.checked })}
              className="w-4 h-4 rounded border-neutral-300 text-primary-600"
            />
            <span className="text-sm text-neutral-700">Recipients (To field)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={localConfig.allowEditSubject !== false}
              onChange={(e) => updateConfig({ allowEditSubject: e.target.checked })}
              className="w-4 h-4 rounded border-neutral-300 text-primary-600"
            />
            <span className="text-sm text-neutral-700">Subject line</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={localConfig.allowEditBody !== false}
              onChange={(e) => updateConfig({ allowEditBody: e.target.checked })}
              className="w-4 h-4 rounded border-neutral-300 text-primary-600"
            />
            <span className="text-sm text-neutral-700">Email body</span>
          </label>
        </div>
      </div>

      {/* Preview */}
      <div className="border rounded-lg p-3 bg-neutral-50">
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          Preview
        </label>
        <div className="bg-surface border rounded p-3 text-sm">
          <div className="mb-1">
            <span className="text-neutral-500">To:</span>{' '}
            <span>{localConfig.defaultTo?.join(', ') || '(empty)'}</span>
          </div>
          <div className="mb-1">
            <span className="text-neutral-500">Subject:</span>{' '}
            <span>{localConfig.defaultSubject || '(empty)'}</span>
          </div>
          <div className="border-t pt-2 mt-2">
            <span className="text-neutral-500">Body:</span>
            <p className="text-neutral-700 whitespace-pre-wrap mt-1">
              {localConfig.defaultBody || '(empty)'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OutlookWidgetBuilder;
